import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { convertDate } from "helpers";
import { PAGINATION_COUNT_STEP_THREE } from "constant/index";
import {
	changeComposition,
	changeEnumListName,
	createAddition,
	createDeadline,
	createEnumList,
	deleteAddition,
	deleteObject,
	getAllEnumList,
	getAvailableObjectList,
	getEnumListObjects,
	getEnumListObjectsShort,
	getMainEnumList,
	uploadDocument,
} from "./stepThree.thunk";
import { IEnumList, IThreeStepObject } from "./stepThree.types";

interface ObjectListSlice {
	id: number | null;
	filtrationValue: string;
	availableObjectList: any[];
	selectionList: any[];
	mainEnumList: IEnumList | null;
	currentEnumList: IEnumList | null;
	allEnumList: IEnumList[];
	enumListObjects: any[];
	enumListObjectsShort: { id: number; obj: { id: number; name: string; reselection: boolean } }[];
	allChecked: boolean | null;
	lastPart: number | null;
}

const initialState: ObjectListSlice = {
	id: null,
	filtrationValue: "",
	availableObjectList: [],
	selectionList: [],
	mainEnumList: null,
	currentEnumList: null,
	allEnumList: [],
	enumListObjects: [],
	enumListObjectsShort: [],
	allChecked: null,
	lastPart: 0,
};

const stepThreeSlice = createSlice({
	name: "stepThreeSlice",
	initialState,
	reducers: {
		ChangeId(state, action: PayloadAction<number>) {
			state.id = action.payload;
			state.availableObjectList = [];
			state.selectionList = [];
			state.mainEnumList = null;
			state.currentEnumList = null;
			state.allEnumList = [];
			state.enumListObjects = [];
		},
		AddToSelectionList(state, action: PayloadAction<any>) {
			state.selectionList.push(action.payload);
			state.allChecked = null;
		},
		RemoveFromSelectionList(state, action: PayloadAction<number>) {
			state.selectionList = state.selectionList.filter((object) => object.id !== action.payload);
			state.allChecked = null;
		},
		ClearSelectionList(state) {
			state.selectionList = [];
		},
		ClearObjectsShort(state) {
			state.enumListObjectsShort = [];
		},
		ChangeCurrentEnumList(state, action: PayloadAction<{ EnumList: IEnumList; value: string }>) {
			state.currentEnumList = action.payload.EnumList;
			state.filtrationValue = action.payload.value;
			state.enumListObjects = [];
			state.lastPart = 0;
		},
		ChangeFiltrationValue(state, action: PayloadAction<string>) {
			state.filtrationValue = action.payload;
		},
		DeactivateEnumListStatus(state) {
			const enumListIndex: number = state.allEnumList.findIndex(
				(enumList: IEnumList) => enumList.id === state.currentEnumList?.id,
			);
			state.allEnumList[enumListIndex].newestDocApprovalDate = "";
			state.currentEnumList!.newestDocApprovalDate = "";
		},
		ChangeChooseAll(state, action: PayloadAction<{ flag: boolean | null; mode: string; arr?: any[] }>) {
			const { flag, arr } = action.payload;
			state.selectionList = [];
			state.allChecked = flag;
			if (flag) {
				if (arr) {
					state.selectionList.push(...arr.map((_) => ({ id: _.id, name: _.obj?.name || _.name })));
				} else {
					state.selectionList.push(...state.availableObjectList);
				}
			}
		},
		resetStep_3: () => initialState,
	},
	extraReducers: (builder) => {
		builder.addCase(getAvailableObjectList.fulfilled, (state, action: PayloadAction<any[]>) => {
			state.availableObjectList = action.payload;
		});
		builder.addCase(getMainEnumList.fulfilled, (state, action) => {
			state.mainEnumList = action.payload[0];
			state.currentEnumList = action.payload[0];
		});
		builder.addCase(createEnumList.fulfilled, (state, action: PayloadAction<{ newEnumList: IEnumList }>) => {
			state.currentEnumList = action.payload.newEnumList;
			state.allEnumList.push(action.payload.newEnumList);
			state.mainEnumList = action.payload.newEnumList;
			state.lastPart = 0;
			state.enumListObjects = [];
		});
		builder.addCase(getAllEnumList.fulfilled, (state, action: PayloadAction<any[]>) => {
			state.allEnumList = action.payload;
		});
		builder.addCase(
			getEnumListObjects.fulfilled,
			(state, action: PayloadAction<{ response: any[]; quantity: number }>) => {
				state.enumListObjects.push(...action.payload.response);

				if (
					action.payload.response.length &&
					state.lastPart !== null &&
					action.payload.response.length === PAGINATION_COUNT_STEP_THREE
				)
					state.lastPart++;
				else state.lastPart = null;
			},
		);
		builder.addCase(getEnumListObjectsShort.fulfilled, (state, action: PayloadAction<any[]>) => {
			state.enumListObjectsShort = action.payload;
		});
		builder.addCase(deleteObject.fulfilled, (state, action: PayloadAction<{ objectId: number; newObject: any }>) => {
			state.enumListObjects = state.enumListObjects.filter((object: any) => object.id !== action.payload.objectId);
			if (action.payload.newObject) state.enumListObjects.push(action.payload.newObject);
		});
		builder.addCase(createDeadline.fulfilled, (state, action: PayloadAction<{ objectId: number; date: string }>) => {
			const objectIndex: number = state.enumListObjects.findIndex(
				(object: any) => object.id === action.payload.objectId,
			);
			state.enumListObjects[objectIndex].categorizationPlanEndDate = action.payload.date;
		});
		builder.addCase(createAddition.fulfilled, (state, action) => {
			state.allEnumList.push(action.payload.newEnumList);
			// state.enumListObjects = action.payload.newEnumListObjects;
			state.enumListObjects = [];
			state.lastPart = 0;
			state.currentEnumList = action.payload.newEnumList;
			state.filtrationValue = `Дополнение в перечень “${action.payload.newEnumList.listName}” от ${convertDate(
				action.payload.newEnumList.creationDate,
			)}`;
		});
		builder.addCase(uploadDocument.fulfilled, (state, action: PayloadAction<string | undefined>) => {
			if (!action.payload) return;
			let currentDate: any = new Date();
			let nextYearDate: any = new Date();
			nextYearDate = new Date(nextYearDate.setFullYear(nextYearDate.getFullYear() + 1));
			if (state.currentEnumList) {
				state.currentEnumList.newestDocApprovalDate = currentDate.toISOString().split("T")[0];
				state.currentEnumList.uploaderName = action.payload;
				state.enumListObjects.forEach((object: IThreeStepObject) => {
					if (!object.categorizationPlanEndDate) {
						object.categorizationPlanEndDate = nextYearDate.toISOString().split("T")[0];
					}
				});
			}
			state.mainEnumList!.newestDocApprovalDate = currentDate.toISOString().split("T")[0];
			state.mainEnumList!.uploaderName = action.payload;
			const enumListIndex: number = state.allEnumList.findIndex(
				(enumList: IEnumList) => enumList.id === state.currentEnumList!.id,
			);
			state.allEnumList[enumListIndex].uploaderName = action.payload;
			state.allEnumList[enumListIndex].newestDocApprovalDate = currentDate.toISOString().split("T")[0];
		});
		builder.addCase(deleteAddition.fulfilled, (state, action: PayloadAction<number>) => {
			state.currentEnumList = state.mainEnumList;
			state.allEnumList = state.allEnumList.filter((enumList: IEnumList) => enumList.id !== action.payload);
			state.enumListObjects = [];
			state.filtrationValue = `Перечень “${state.mainEnumList?.listName}” от ${convertDate(
				state.mainEnumList!.creationDate,
			)}`;
		});
		builder.addCase(changeEnumListName.fulfilled, (state, action: PayloadAction<string>) => {
			state.filtrationValue = state.filtrationValue.replace(`${state.currentEnumList?.listName}`, action.payload);
			if (state.currentEnumList) state.currentEnumList.listName = action.payload;
			const enumListIndex: number = state.allEnumList.findIndex(
				(enumList: IEnumList) => enumList.id === state.currentEnumList?.id,
			);
			state.allEnumList[enumListIndex].listName = action.payload;
			if (state.mainEnumList && state.mainEnumList.id === state.currentEnumList?.id)
				state.mainEnumList.listName = action.payload;
		});
		builder.addCase(changeComposition.fulfilled, (state, action: PayloadAction<any[]>) => {
			state.enumListObjects = [];
			state.lastPart = 0;

			state.enumListObjects.push(...action.payload);

			if (action.payload.length && state.lastPart !== null && action.payload.length === PAGINATION_COUNT_STEP_THREE)
				state.lastPart++;
			else state.lastPart = null;
		});
	},
});

export const {
	ChangeId,
	AddToSelectionList,
	RemoveFromSelectionList,
	ClearSelectionList,
	ClearObjectsShort,
	ChangeCurrentEnumList,
	ChangeFiltrationValue,
	DeactivateEnumListStatus,
	ChangeChooseAll,
	resetStep_3,
} = stepThreeSlice.actions;
export default stepThreeSlice.reducer;
